/**
 * #2208 (diag #49/#50/#53) — human geolocation error messages.
 *
 * The confusing case: the Permissions API reports permission='granted' but the
 * actual getCurrentPosition/watchPosition call still returns PERMISSION_DENIED
 * (code 1). On iOS/macOS Safari that means OS-level Location Services is OFF for
 * the browser — the SITE permission is on, but the system denies it. Reps (e.g.
 * Jordan Goodman, who failed on both his iPhone AND Mac) are sure they granted
 * everything, so a generic "permission denied" is misleading. Give device-aware
 * guidance instead.
 *
 * Pure + framework-free → unit-tested.
 */

export type GeoPlatform = 'ios' | 'macos' | 'android' | 'other';

/** Geolocation API error codes (GeolocationPositionError). */
export const GEO_PERMISSION_DENIED = 1;
export const GEO_POSITION_UNAVAILABLE = 2;
export const GEO_TIMEOUT = 3;

export function detectGeoPlatform(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): GeoPlatform {
  // iPadOS reports as Macintosh but has touch; treat touch-Macs as iOS-style.
  const isTouchMac =
    /Macintosh/.test(ua) && typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 1;
  if (/iPhone|iPad|iPod/.test(ua) || isTouchMac) return 'ios';
  if (/Macintosh|Mac OS X/.test(ua)) return 'macos';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

const OS_STEPS: Record<GeoPlatform, string> = {
  ios: 'On iPhone: Settings → Privacy & Security → Location Services (turn ON), then scroll to Safari Websites → set to “While Using the App.”',
  macos: 'On Mac: System Settings → Privacy & Security → Location Services (turn ON) and make sure Safari is enabled.',
  android: 'On Android: Settings → Location (turn ON), and allow location for your browser.',
  other: 'Turn on Location Services for your browser in your device settings.',
};

export interface GeoErrorContext {
  /** Permissions API state at error time — 'granted' means the site permission is on. */
  permissionGranted?: boolean;
  platform?: GeoPlatform;
}

/**
 * True when the error is the "site permission granted but OS denies" case —
 * the signal that OS Location Services is off for the browser.
 */
export function isOsLocationServicesOff(code: number, ctx: GeoErrorContext = {}): boolean {
  return code === GEO_PERMISSION_DENIED && ctx.permissionGranted === true;
}

export function buildGeoErrorMessage(code: number, ctx: GeoErrorContext = {}): string {
  const platform = ctx.platform ?? 'other';
  if (code === GEO_PERMISSION_DENIED) {
    if (ctx.permissionGranted) {
      return `Your in-app location permission is on, but your device’s Location Services appear to be turned off for your browser. ${OS_STEPS[platform]}`;
    }
    return 'Location permission denied. Please allow location access when your browser prompts you.';
  }
  if (code === GEO_POSITION_UNAVAILABLE) return 'Location unavailable right now. Move to an open area and try again.';
  if (code === GEO_TIMEOUT) return 'Location request timed out. Please try again.';
  return 'Unknown location error.';
}
